import React, { ReactElement, useEffect, useState } from "react";
import Head from "next/head";
import { appWithTranslation } from "next-i18next";
import "../styles/app.scss";
import { Provider } from "react-redux";
import Navbar from "../src/components/layout/navbar/Navbar";
import Footer from "../src/components/layout/footer/Footer";
import { AppProps } from "next/app";
import { AppStore, store } from "../src/store"
import ErrorDialog from "../src/features/errors/ErrorDialog";
import { useAppSelector } from "../src/hooks";
import { theme } from "../src/theme/theme";
import { ThemeProvider } from "@emotion/react";
import { logger } from "../src/helper/logger";
import { NODE_ENV } from "../src/config";
import { isScaVariant } from "../src/helper/utils";
import { isRunningInBrowser } from "../src/helper/ssr";
import * as R from "ramda";
import { GetStaticProps } from "next";
import { getLanguageStaticProps } from "../src/helper/languageHelper";
import styled from "@emotion/styled";
import t from "../src/helper/t";
import LoggerView from "../src/components/layout/logger/LoggerView";
import PrivacyPolicyDialog from "../src/components/elements/PrivacyPolicyDialog";
import { ISpeedtestPlus } from "../src/types";
import { cancel, isSpeedtestRunning, Phase } from "../src/features/measurement/measurementSlice";
import { currentSubscription } from "../src/networkQuality";
import { UAParser } from "ua-parser-js";
import { getIsSupported, OsName } from "../src/helper/utils/compatibility";
import watch from "redux-watch";

declare global {
  interface Window {
    SpeedtestPlus: ISpeedtestPlus;
    VF: {
      tealiumUseDCF?: boolean,
      tealiumEnvironment?: string,
    };
    store: AppStore;
    //TODO: use store.dispatch() instead setBeforeUnload
    setBeforeUnload: () => void;
    webkitConvertPointFromNodeToPage: unknown;
  }
}

if (isRunningInBrowser()) {

  if (isScaVariant()) {
    const isSpeedtestRunningGlobal = () => {
      const state = store.getState().measurement;
      logger.debug(`isSpeedtestRunningGlobal state.kind = ${state.kind}`);
      return isSpeedtestRunning(state);
    }

    window.SpeedtestPlus = {
      isSpeedtestRunning: (): boolean => {
        return isSpeedtestRunningGlobal();
      },

      cancelSpeedtest: (): Promise<void> => {
        store.dispatch(cancel(currentSubscription.subscription));

        return new Promise((resolve, reject) => {
          const w = watch(() => store.getState().measurement.kind);
          store.subscribe(w((newVal: Phase) => {
            if (newVal === Phase.NotStarted) {
              resolve();
            }
          }));

          if (store.getState().measurement.kind === Phase.NotStarted) {
            resolve();
          }
        });
      }
    }
  }

  if (!isScaVariant() && !R.isNil(window["VF"])) {
    if (NODE_ENV !== "production") {
      window["VF"]["tealiumEnvironment"] = "autotest";
    }
    window["VF"]["tealiumUseDCF"] = true;
    logger.info("Tealium setup");
  }
}

const Container = styled.div([
  {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridTemplateRows: "auto 1fr",
    gridTemplateAreas: "'header' 'main'",
    height: "100vh"
  }
]);

const Header = styled.div([
  {
    gridArea: "header"
  }
]);

const Main = styled.div([
  {
    overflow: "auto",
    gridArea: "main"
  }
]);

enum ViewKind {
  None,
  Default,
  Admin,
  OldBrowser,
}

const App = ({ Component, pageProps, router }: AppProps): ReactElement => {
  const [viewKind, setViewKind] = useState(ViewKind.Default);

  useEffect(() => {
    if (router.route === "/RgyhV5aRSi") {
      setViewKind(ViewKind.Admin);
    } else {
      const userAgent = navigator.userAgent;
      if (getIsSupported(userAgent)) {
        setViewKind(ViewKind.Default);
      } else {
        setViewKind(ViewKind.OldBrowser);
      }
    }
  }, []);

  switch (viewKind) {
    case ViewKind.None:
      return <NonePage />
    case ViewKind.Admin:
      return <AdminPage pageProps={pageProps} Component={Component} router={router} />
    case ViewKind.OldBrowser:
      return <OldBrowserPage />
    case ViewKind.Default:
      return <DefaultPage pageProps={pageProps} Component={Component} router={router} />
  }
};

const NonePage = (): ReactElement => {
  return (
    <></>
  );
}

const AdminPage = ({ Component, pageProps }: AppProps): ReactElement => {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>

      </ThemeProvider>
      <main style={{ height: "auto" }}>
        <Component {...pageProps} />
        <ErrorDialogSwitcher />
      </main>
    </Provider>
  );
}

const OldBrowserPage = (): ReactElement => {

  const userAgent = navigator.userAgent;
  const parser = new UAParser(userAgent);
  const result = parser.getResult();
  const iOsName: OsName = "iOS";

  const getFirstLine = () => {
    if (result.os.name === iOsName) {
      return t("OldBrowser_iOS");
    }

    return t(isScaVariant() ? "OldBrowser_SCA" : "OldBrowser_WEB");
  }

  const getSecondLine = () => {
    if (result.os.name === iOsName) {
      return "";
    }

    return isScaVariant() ? `${result.os.name} ${result.os.version}` : `${result.browser.name} ${result.browser.version}`;
  }

  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <Head>
          <title>{t("Title")}</title>
          <meta name="description" content={t("Description")}></meta>
          <meta name="revisit-after" content="1 days"></meta>
          <meta name="referrer" content="no-referrer"></meta>
        </Head>
        <Container>
          <Header>
          </Header>

          <Main>
            <main>
              <div style={{ margin: 12 }}>
                <p>{getFirstLine()}</p>
                <p>
                  {getSecondLine()}
                </p>
              </div>
              <ErrorDialogSwitcher />
            </main>
          </Main>
        </Container>
      </ThemeProvider>
    </Provider>
  );
}

const DefaultPage = ({ Component, pageProps }: AppProps): ReactElement => {

  //workaround for disabled JS
  const [isWeb, setIsWeb] = useState(false);
  useEffect(() => {
    setIsWeb(!isScaVariant());
  }, []);

  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <Head>
          <title>{t("Title")}</title>
          <meta name="description" content={t("Description")}></meta>
          <meta name="revisit-after" content="1 days"></meta>
          <meta name="referrer" content="no-referrer"></meta>
          {!isScaVariant() && (NODE_ENV !== "development") && (
            <>
              <script type="text/javascript" src="/s_code_setup.js"></script>
              <script type="text/javascript" src="//www.vodafone.de/scripts/s_code.js"></script>
            </>
          )}
        </Head>
        <Container>
          <Header>
            {isWeb && (
              <Navbar />
            )}
          </Header>

          <Main>
            <main>
              <Component {...pageProps} />
              <ErrorDialogSwitcher />
              <LoggerViewSwitcher />
              <PrivacyPolicyDialogSwitcher />
            </main>
            {isWeb && (
              <Footer />
            )}
          </Main>
        </Container>
      </ThemeProvider>
    </Provider>
  );
}

const PrivacyPolicyDialogSwitcher: React.FC<{}> = () => {
  const privacyPolicyState = useAppSelector(state => state.privacyPolicy)
  if (privacyPolicyState.privacyPolicyDialogVisible) {
    return <PrivacyPolicyDialog />
  } else {
    return null
  }
}

const ErrorDialogSwitcher: React.FC<{}> = () => {
  const errorState = useAppSelector(state => state.error)
  if (errorState.lastError != null) {
    return <ErrorDialog lastError={errorState.lastError} />
  } else {
    return null
  }
}

const LoggerViewSwitcher: React.FC<{}> = () => {
  const errorState = useAppSelector(state => state.debugLog)
  if (errorState.showDebugLog) {
    return <LoggerView />
  } else {
    return null
  }
}

export const getStaticProps: GetStaticProps = (context) => {
  return getLanguageStaticProps(context, ["common"]);
};

export default appWithTranslation(App);
